import React, { useState } from 'react'
import styles from './DocflowTable.module.scss'

const STATUS_COLORS = {
  draft:     { bg: '#f1f5f9', color: '#64748b' },
  pending:   { bg: '#fef9c3', color: '#854d0e' },
  signing:   { bg: '#dbeafe', color: '#1d4ed8' },
  approved:  { bg: '#dcfce7', color: '#15803d' },
  returned:  { bg: '#fee2e2', color: '#b91c1c' },
  cancelled: { bg: '#f3f4f6', color: '#6b7280' },
}

const STATUS_LABELS = {
  draft:     'Черновик',
  pending:   'На рассмотрении',
  signing:   'На подписании',
  approved:  'Утверждён',
  returned:  'Возвращён',
  cancelled: 'Отменён',
}

function fmtDate(str) {
  if (!str) return '—'
  const d = new Date(str)
  if (isNaN(d.getTime())) return str
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

const DOC_TYPE_ICONS = {
  business_plan: '📊', application: '📝', appeal: '📣',
  service_note: '📋', notification: '🔔', order: '📜',
  instruction: '📖', report: '📈', contract: '🤝',
  reference: '📄', memo: '🗒️', explanatory: '✍️',
  protocol: '🏛️', other: '📂',
}

export default function DocflowTable({ docs, mode, onSelect }) {
  const [statusFilter, setStatusFilter] = useState('')
  const [search, setSearch]             = useState('')

  const filtered = (docs || []).filter(d => {
    if (statusFilter && d.status !== statusFilter) return false
    if (search) {
      const q = search.toLowerCase()
      return (
        d.subject?.toLowerCase().includes(q) ||
        d.reg_number?.toLowerCase().includes(q) ||
        d.from_employee_name?.toLowerCase().includes(q) ||
        d.to_employee_name?.toLowerCase().includes(q)
      )
    }
    return true
  })

  const unreadCount = (docs || []).filter(d => !d.is_read).length

  return (
    <div className={styles.wrap}>
      {/* ── Toolbar ── */}
      <div className={styles.toolbar}>
        {unreadCount > 0 && (
          <span className={styles.unreadBadge}>{unreadCount} непрочитанных</span>
        )}
        <input
          className={styles.search}
          placeholder="Поиск по теме, номеру, заявителю…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className={styles.filter}
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
        >
          <option value="">Все статусы</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {/* ── Table ── */}
      {!docs && <p className={styles.loading}>Загрузка…</p>}
      {docs && !filtered.length && (
        <div className={styles.empty}>
          <span>📭</span>
          <p>{search || statusFilter ? 'Ничего не найдено' : 'Документов нет'}</p>
        </div>
      )}
      {docs && filtered.length > 0 && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th style={{ width: 24 }}></th>
                <th>Номер</th>
                <th>{mode === 'outbox' ? 'Получатель' : 'Заявитель'}</th>
                <th>Тема</th>
                <th>Статус</th>
                {mode === 'outbox' && <th>Подписание</th>}
                <th>Дата</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(doc => {
                const unread   = !doc.is_read
                const sc       = STATUS_COLORS[doc.status] || STATUS_COLORS.draft
                const typeIcon = DOC_TYPE_ICONS[doc.doc_type] || '📄'
                const party    = mode === 'outbox'
                  ? doc.to_employee_name
                  : doc.from_employee_name
                const partyDept = mode === 'outbox' ? '' : doc.from_employee_dept

                return (
                  <tr
                    key={doc.id}
                    className={`${styles.row} ${unread ? styles.unread : ''}`}
                    onClick={() => onSelect(doc.id)}
                  >
                    {/* unread dot */}
                    <td className={styles.dotCell}>
                      {unread && <span className={styles.dot} />}
                    </td>

                    {/* reg number + type */}
                    <td className={styles.numCell}>
                      <span className={styles.typeIcon}>{typeIcon}</span>
                      <div>
                        <span className={styles.regNum}>{doc.reg_number}</span>
                        <span className={styles.docType}>{doc.doc_type_display}</span>
                      </div>
                    </td>

                    {/* party */}
                    <td className={styles.partyCell}>
                      <span className={styles.partyName}>{party}</span>
                      {partyDept && <span className={styles.partyDept}>{partyDept}</span>}
                    </td>

                    {/* subject */}
                    <td className={styles.subjectCell}>
                      <span className={unread ? styles.subjectBold : styles.subjectNormal}>
                        {doc.subject}
                      </span>
                    </td>

                    {/* status badge */}
                    <td>
                      <span
                        className={styles.statusBadge}
                        style={{ background: sc.bg, color: sc.color }}
                      >
                        {doc.status_display}
                      </span>
                    </td>

                    {/* signing progress (outbox only) */}
                    {mode === 'outbox' && (
                      <td className={styles.signingCell}>
                        {doc.signers_count > 0 ? (
                          <span className={styles.sigProgress}>
                            {doc.signed_count}/{doc.signers_count} подписали
                          </span>
                        ) : (
                          <span className={styles.noSig}>—</span>
                        )}
                      </td>
                    )}

                    {/* date */}
                    <td className={styles.dateCell}>{fmtDate(doc.created_at)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
